import { z } from 'zod';

const NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 '&.,()-]*$/;
const PHONE_PATTERN = /^\+?[1-9]\d{9,14}$/;
const PASSWORD_LOWERCASE_PATTERN = /[a-z]/;
const PASSWORD_UPPERCASE_PATTERN = /[A-Z]/;
const PASSWORD_NUMBER_PATTERN = /\d/;
const PASSWORD_SPECIAL_PATTERN = /[^A-Za-z0-9]/;

export const AGENT_STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

export const LEAD_LOAN_TYPE_OPTIONS = [
  { value: 'home', label: 'Home Loan' },
  { value: 'personal', label: 'Personal Loan' },
  { value: 'business', label: 'Business Loan' },
  { value: 'car', label: 'Car Loan' },
  { value: 'education', label: 'Education Loan' },
  { value: 'other', label: 'Other' },
];

export const LEAD_SOURCE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'agent', label: 'Agent' },
  { value: 'walkin', label: 'Walk-in' },
];

export const LEAD_PRIORITY_OPTIONS = [
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
];

export const EMPLOYEE_DEFAULT_VALUES = {
  name: '',
  email: '',
  password: '',
  phone: '',
  employee_role_id: '',
  department_id: '',
};

export const AGENT_DEFAULT_VALUES = {
  name: '',
  email: '',
  password: '',
  phone: '',
  target: '',
  status: 'Active',
};

export const LEAD_DEFAULT_VALUES = {
  name: '',
  phone: '',
  email: '',
  loanType: 'home',
  amount: '',
  source: 'admin',
  priority: 'Medium',
};

export function normalizeWhitespace(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

export function sanitizeEmail(value) {
  return normalizeWhitespace(value).toLowerCase();
}

export function sanitizePhone(value) {
  const normalized = normalizeWhitespace(value);
  if (!normalized) return '';
  let sanitized = normalized.replace(/[\s().-]/g, '');
  if (sanitized.startsWith('00')) {
    sanitized = `+${sanitized.slice(2)}`;
  }
  return sanitized;
}

function normalizeNumericInput(value) {
  if (value === null || value === undefined) return '';
  return normalizeWhitespace(value);
}

const nameSchema = z
  .string()
  .transform(normalizeWhitespace)
  .pipe(
    z
      .string()
      .min(2, 'Must be at least 2 characters.')
      .max(150, 'Must be 150 characters or fewer.')
      .refine((value) => NAME_PATTERN.test(value), 'Use letters, numbers, spaces, and standard punctuation only.')
  );

const emailSchema = z
  .string()
  .transform(sanitizeEmail)
  .pipe(z.string().min(1, 'Email is required.').email('Enter a valid email address.').max(254, 'Email is too long.'));

const optionalEmailSchema = z
  .string()
  .default('')
  .transform(sanitizeEmail)
  .refine((value) => !value || z.string().email().safeParse(value).success, 'Enter a valid email address.')
  .refine((value) => value.length <= 254, 'Email is too long.');

const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters.')
  .max(72, 'Password must be 72 characters or fewer.')
  .refine((value) => PASSWORD_LOWERCASE_PATTERN.test(value), 'Password must include at least one lowercase letter.')
  .refine((value) => PASSWORD_UPPERCASE_PATTERN.test(value), 'Password must include at least one uppercase letter.')
  .refine((value) => PASSWORD_NUMBER_PATTERN.test(value), 'Password must include at least one number.')
  .refine((value) => PASSWORD_SPECIAL_PATTERN.test(value), 'Password must include at least one special character.');

const optionalPhoneSchema = z
  .string()
  .default('')
  .transform(sanitizePhone)
  .refine((value) => !value || PHONE_PATTERN.test(value), 'Enter a valid phone number with 10 to 15 digits.');

const requiredPhoneSchema = optionalPhoneSchema.refine((value) => Boolean(value), 'Phone number is required.');

export const employeeCreateSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  password: passwordSchema,
  phone: optionalPhoneSchema,
  employee_role_id: z.string().default('').transform(normalizeWhitespace),
  department_id: z.string().default('').transform(normalizeWhitespace),
});

export const agentCreateSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  password: passwordSchema,
  phone: requiredPhoneSchema,
  target: z
    .preprocess((value) => normalizeNumericInput(value), z.string())
    .transform((value) => {
      if (!value) return 0;
      return Number(value);
    })
    .refine((value) => Number.isFinite(value), 'Enter a valid target.')
    .refine((value) => value >= 0, 'Target cannot be negative.')
    .refine((value) => value <= 1000000, 'Target is too large.')
    .transform((value) => Math.trunc(value)),
  status: z.enum(AGENT_STATUS_OPTIONS.map((item) => item.value), {
    errorMap: () => ({ message: 'Select a valid status.' }),
  }),
});

export const leadCreateSchema = z.object({
  name: nameSchema,
  phone: requiredPhoneSchema,
  email: optionalEmailSchema,
  loanType: z.enum(LEAD_LOAN_TYPE_OPTIONS.map((item) => item.value), {
    errorMap: () => ({ message: 'Select a valid loan type.' }),
  }),
  amount: z
    .preprocess((value) => normalizeNumericInput(value), z.string())
    .refine((value) => Boolean(value), 'Loan amount is required.')
    .refine((value) => Number.isFinite(Number(value)), 'Enter a valid loan amount.')
    .transform((value) => Number(value))
    .refine((value) => value > 0, 'Loan amount must be greater than 0.')
    .refine((value) => value <= 1000000000, 'Loan amount is too large.'),
  source: z.enum(LEAD_SOURCE_OPTIONS.map((item) => item.value), {
    errorMap: () => ({ message: 'Select a valid source.' }),
  }),
  priority: z.enum(LEAD_PRIORITY_OPTIONS.map((item) => item.value), {
    errorMap: () => ({ message: 'Select a valid priority.' }),
  }),
});

export function buildEmployeeCreatePayload(values) {
  const parsed = employeeCreateSchema.parse(values);
  return {
    full_name: parsed.name,
    email: parsed.email,
    password: parsed.password,
    phone: parsed.phone || null,
    status: 'active',
    department_id: parsed.department_id || null,
    employee_role_id: parsed.employee_role_id || null,
  };
}

export function buildAgentCreatePayload(values) {
  const parsed = agentCreateSchema.parse(values);
  return {
    full_name: parsed.name,
    email: parsed.email,
    password: parsed.password,
    phone: parsed.phone,
    target: parsed.target,
    status: parsed.status === 'Active' ? 'active' : 'inactive',
  };
}

export function buildLeadCreatePayload(values) {
  const parsed = leadCreateSchema.parse(values);
  return {
    customer_name: parsed.name,
    phone: parsed.phone,
    email: parsed.email || null,
    loan_type: parsed.loanType,
    loan_amount: parsed.amount,
    status: 'new',
    source: parsed.source,
    priority: parsed.priority,
  };
}

const ENTITY_API_FIELD_MAP = {
  full_name: 'name',
  email: 'email',
  password: 'password',
  phone: 'phone',
  department_id: 'department_id',
  employee_role_id: 'employee_role_id',
  customer_name: 'name',
  loan_type: 'loanType',
  loan_amount: 'amount',
  source: 'source',
  priority: 'priority',
  status: 'status',
  target: 'target',
};

export function mapEntityApiErrors(error) {
  const fieldErrors = {};
  const apiFieldErrors = error?.fieldErrors || {};

  Object.entries(apiFieldErrors).forEach(([key, message]) => {
    const field = ENTITY_API_FIELD_MAP[key];
    if (field && message) {
      fieldErrors[field] = String(message);
    }
  });

  const message = String(error?.message || '');
  if (/email already registered/i.test(message)) {
    fieldErrors.email = 'This email is already in use.';
  }
  if (/invalid department/i.test(message)) {
    fieldErrors.department_id = 'Select a valid department.';
  }
  if (/invalid employee role/i.test(message)) {
    fieldErrors.employee_role_id = 'Select a valid role.';
  }
  if (/invalid employee assignment/i.test(message)) {
    fieldErrors.source = 'The selected assignment is invalid for your scope.';
  }
  if (/invalid assigned_to user/i.test(message) || /assigned user not found/i.test(message)) {
    fieldErrors.source = 'The selected assignee is invalid.';
  }

  return fieldErrors;
}

export function getEntityErrorMessage(error, fallbackMessage) {
  const message = String(error?.message || '').trim();
  if (!message || message === '[object Object]') return fallbackMessage;
  if (/email already registered/i.test(message)) return 'Email already registered.';
  if (/invalid department/i.test(message)) return 'Selected department is invalid.';
  if (/invalid employee role/i.test(message)) return 'Selected role is invalid.';
  return message;
}
